
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Eye } from "lucide-react";

type AccessLog = {
  id: string;
  accessed_at: string;
  user_display_name: string;
  accessed_by: string | null;
};

type AccessLogsProps = {
  entryId: string;
  isCreator: boolean;
  isPublic: boolean;
};

export const AccessLogs: React.FC<AccessLogsProps> = ({ entryId, isCreator, isPublic }) => {
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccessLogs();
  }, [entryId]);

  const fetchAccessLogs = async () => {
    if (!isCreator && !isPublic) return;
    
    try {
      const { data, error } = await supabase
        .from("entry_access_logs")
        .select("*")
        .eq("entry_id", entryId)
        .order("accessed_at", { ascending: false })
        .limit(10);

      if (error) {
        console.error("Error fetching access logs:", error);
        return;
      }

      setAccessLogs(data || []);
    } catch (error) {
      console.error("Error in fetchAccessLogs:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || (!isCreator && !isPublic) || accessLogs.length === 0) {
    return null;
  }

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4" />
          Recent Access History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {accessLogs.map((log) => (
            <div key={log.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {new Date(log.accessed_at).toLocaleDateString()} at{" "}
                  {new Date(log.accessed_at).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
                <span>by</span>
                <Badge variant={log.user_display_name === "Unknown" ? "outline" : "secondary"}>
                  {log.user_display_name === "Unknown" ? "Unknown" : `@${log.user_display_name}`}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
